import {
  addDrawerListeners,
  basePathRewrite,
  cetgoriesCount,
  checkForGuid,
  checkIsFreeJobAvailable,
  commonPropsForSSR,
  ensureVisitorId,
  extractCountryCode,
  getAbsoluteURL,
  getDrawerPopCount,
  getEBendPoints,
  getEJendPoints,
  getFreeJobViewQuota,
  getfullUrl,
  getHostValue,
  getRmcCategories,
  handleDrawerScroll,
  readCookie,
  readFeatureCookieByKey,
  response410,
  setFeatureCookieByKey,
} from '@components/common';
import companiesMapping from '@components/common/companiesMapping.json';
import {
  ApplyJobStatus,
  getScrollHeightDrawerExp,
} from '@components/common/constant';
import {
  addJobsCountTraits,
  jobSearchResultTrackEvent,
} from '@components/common/event-tracker';
import rmcCategoriesObj from '@components/common/rmcCategories.json';
import { SiteNavigationSchemaScript } from '@components/metaComponent/siteNavigationSchema';
import { cdnEXPTType, IJobCount } from '@components/shared.types';
import {
  JobDetailsPage,
  JobSearchSteps,
  useDeviceType,
  useLandingPageTracking,
} from '@license-admin/boldjobs';
import { fireExperimentEvent } from '@utils/cdnTracking';
import { createFaqSchema, generateFaqItems } from '@utils/faqSchemaGenerator';
import jobDetailsPageProps from '@utils/jobDetailsPage';
import jobSearchStepsProps from '@utils/jobSearchStepsProps';
import { processJobDetailsForExperiment } from '@utils/showDetails';
import { getJobDetailsV2, normalizeJobDetailsV2 } from 'API/ApiHelpers';
import {
  applyJob,
  getCompaniesHiringCount,
  getSimilarJobs,
  jobViewed,
  modifyJobDetails,
} from 'API/jobs';
import { apiCallList } from 'API/wlb/ApiClient';
import {
  getFreeJobsByCategory,
  getFreeRemoteJobsByCategory,
} from 'API/wlb/jobs';
import { getENVPath, getSchemaWiseEducationLevel, isProdEnv } from 'helper';
import {
  getGuidValue,
  getOrUpdateGUID,
  getSearchType,
} from 'helper/eventTrackingUtils';
import {
  Action,
  SearchType,
  USER_TRAITS_JOBS_COUNT,
  ViewType,
} from 'helper/eventTrackingUtils/constant';
import {
  ConductExperimentV3,
  getExperimentVariant,
} from 'helper/experiments/experimentActions';
import { RMCPortalExperiments } from 'helper/experiments/experimentConstants';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useCallback, useEffect, useState } from 'react';
import ErrorPages from 'src/pages/_error';
// Interfaces
interface IProps {
  jobDetails: any;
  categoryBasedSchema?: boolean;
  similarJobCategoryName: string;
  url: string;
  host: string;
  isPremiumUser?: boolean;
  isLoggedIn?: boolean;
  themeType?: string;
  isFreeJob?: boolean;
  deviceType: string;
  userAgent: string;
  localization?: any;
  cdnExptData?: cdnEXPTType;
  category?: any;
  isSecondaryModalPopup?: boolean;
  hiring_count?: IJobCount;
  similarJobsItem?: any;
  userInteractExpId?: number;
  isMatchFreeSchemaJob?: boolean;
}

// Helper Functions
const sanitizeDescription = (description: string) => {
  return description
    .replace(/"/g, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]*>?/gm, '');
};
const getIndustryFromJobCategories = (categories: any[]) => {
  // industry/categories
  if (categories?.length > 0) {
    const rmcCategories = getRmcCategories(categories);
    const industry = rmcCategories
      ?.map((category: any) => category.name)
      .filter(Boolean);

    if (industry?.length > 0) {
      return industry;
    }
  }
  return [];
};
const sanitizeCompanyDescription = (description: string) => {
  return description
    .replace(/<\/?p[^>]*>/gi, '') // Remove paragraph tags
    .replace(/'/g, '&#39;') // Replace single quotes with HTML entity
    .replace(/"/g, '&quot;'); // Replace double quotes with HTML entity
};

const parseSalaryRange = (salaryRange: string) => {
  if (!salaryRange)
    return { minSalary: 0.0, maxSalary: null, salaryUnitText: '' };
  const range = salaryRange.split(' ') || [];
  if (range.length > 1) {
    const cleanedData = range
      .map((item) => item.replace(/-/g, '')) // Remove hyphens
      .filter((item) => item !== ''); // Remove empty strings

    // Extract numeric values, properly handling commas in numbers
    const minSalaryStr = cleanedData[0]?.replace(/,/g, '');
    const maxSalaryStr = cleanedData[1]?.replace(/,/g, '');

    const salaryUnitText = cleanedData[cleanedData.length - 1] || '';

    return {
      minSalary: minSalaryStr ? parseInt(minSalaryStr, 10) || 0 : 0,
      maxSalary: maxSalaryStr ? parseInt(maxSalaryStr, 10) : null,
      salaryUnitText: salaryUnitText,
    };
  }

  return { minSalary: 0.0, maxSalary: null, salaryUnitText: '' };
};
const createJobPostingObject = (
  jobDetails: any,
  host: string,
  salaryData: any
) => {
  const pattern = /US National|USA|United States/g;

  // Social profiles array for schema
  const socialProfiles: string[] = [
    ...(jobDetails?.company?.facebook
      ? [
          jobDetails.company.facebook.startsWith('http')
            ? jobDetails.company.facebook
            : `https://facebook.com/${jobDetails.company.facebook}`,
        ]
      : []),
    ...(jobDetails?.company?.twitter
      ? [
          jobDetails.company.twitter.startsWith('http')
            ? jobDetails.company.twitter
            : `https://x.com/${jobDetails.company.twitter}`,
        ]
      : []),
    ...(jobDetails?.company?.linkedin
      ? [
          jobDetails.company.linkedin.startsWith('http')
            ? jobDetails.company.linkedin
            : `https://linkedin.com/company/${jobDetails.company.linkedin}`,
        ]
      : []),
    ...(jobDetails?.company?.instagram
      ? [
          jobDetails.company.instagram.startsWith('http')
            ? jobDetails.company.instagram
            : `https://instagram.com/${jobDetails.company.instagram}`,
        ]
      : []),
  ].filter(Boolean);
  const hasMinSalary =
    salaryData.maxSalary == null || isNaN(salaryData.maxSalary);
  const hasMaxSalary =
    salaryData.minSalary == null || isNaN(salaryData.minSalary);

  const QuantitativeValueObj =
    hasMinSalary || hasMaxSalary
      ? {
          '@type': 'QuantitativeValue',
          value: salaryData.minSalary || salaryData.maxSalary,
          unitText: salaryData.salaryUnitText,
        }
      : {
          '@type': 'QuantitativeValue',
          minValue: salaryData.minSalary,
          maxValue: salaryData.maxSalary,
          unitText: salaryData.salaryUnitText,
        };

  const jobPostingObj: any = {
    '@type': 'JobPosting',
    title: jobDetails?.title || '',
    ...(jobDetails?.description && {
      description: sanitizeDescription(jobDetails?.description || ''),
    }),
    datePosted: jobDetails?.postedDate,
    validThrough: jobDetails?.expireOn,
    // category/industry
    ...(jobDetails?.category && { industry: jobDetails.category }),
    // applicantLocationRequirements
    applicantLocationRequirements: {
      '@type': 'Country',
      name: extractCountryCode(jobDetails?.countries) || '',
    },
    employmentType: jobDetails?.jobSchedules?.[0] || '',
    industry: getIndustryFromJobCategories(jobDetails?.categories),
    identifier: {
      '@type': 'PropertyValue',
      name: jobDetails?.company?.name || 'Remote.co',
      value: jobDetails?.id?.toString() || '',
    },
    hiringOrganization: {
      '@type': 'Organization',
      name: jobDetails?.company?.name || '',
      ...(jobDetails?.company?.logo && {
        logo: `https://${host}${jobDetails?.company?.logo}`,
      }),
      ...((jobDetails?.company?.website || jobDetails?.company?.url) && {
        url: jobDetails?.company?.website || jobDetails?.company?.url,
      }),
      ...(jobDetails?.company?.summary && {
        description: sanitizeCompanyDescription(jobDetails?.company?.summary),
      }),
      ...(socialProfiles.length > 0 && { sameAs: socialProfiles }), // social profiles
      ...(jobDetails?.company?.city &&
        jobDetails?.company?.state &&
        jobDetails?.company?.country && {
          address: {
            '@type': 'PostalAddress',
            addressLocality:
              jobDetails?.company?.city?.replace(pattern, 'US') || '',
            addressRegion: jobDetails?.company?.state || '',
            addressCountry:
              extractCountryCode(jobDetails?.company?.country) || '',
          },
        }),
    },
  };

  // job id or indentifier
  if (jobDetails?.id) {
    jobPostingObj['identifier'] = jobDetails?.id?.toString() || '';
  }
  // jobEducationRequirements
  let educationObj: any = [];
  // jobEducationRequirements
  if (jobDetails?.educationLevels) {
    let educationLevels = jobDetails.educationLevels || [];
    if (educationLevels) {
      educationLevels.forEach((level: string) => {
        let edulvl = getSchemaWiseEducationLevel(level);
        if (edulvl) {
          educationObj.push({
            '@type': 'EducationalOccupationalCredential',
            credentialCategory: edulvl,
          });
        }
      });
    }
  }
  if (educationObj.length > 0) {
    //code to remove duplicate credentialCategory from educationObj
    const uniqueEducationObj = Array.from(
      new Map(
        educationObj.map((item: any) => [item['credentialCategory'], item])
      ).values()
    );
    jobPostingObj['educationRequirements'] = uniqueEducationObj;
  }

  // jobLocation latitude and longitude
  if (
    jobDetails?.latitude ||
    jobDetails?.longitude ||
    jobDetails?.company?.latitude ||
    jobDetails?.company?.longitude
  ) {
    jobPostingObj['latitude'] =
      jobDetails?.company?.latitude || jobDetails?.latitude || '';
    jobPostingObj['longitude'] =
      jobDetails?.company?.longitude || jobDetails?.longitude || '';
  }

  if (
    Array.isArray(jobDetails?.remoteOptions) &&
    jobDetails?.remoteOptions.some((opt: string) => typeof opt === 'string')
  ) {
    jobPostingObj['jobLocationType'] = 'TELECOMMUTE';
  }
  if (Array.isArray(jobDetails?.cities) && jobDetails?.cities.length > 0) {
    jobPostingObj['jobLocation'] = {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: jobDetails?.cities?.[0]?.replace(pattern, 'US') || '',
        addressCountry: extractCountryCode(jobDetails?.countries) || '',
      },
      ...(jobDetails?.latitude != null &&
        jobDetails?.longitude != null && {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: jobDetails?.latitude,
            longitude: jobDetails?.longitude,
          },
        }),
    };
  }

  // baseSalary
  if (QuantitativeValueObj.unitText !== '') {
    jobPostingObj['baseSalary'] = {
      '@type': 'MonetaryAmount',
      currency:
        jobDetails?.salaryCurrency || jobDetails?.salaryRange !== ''
          ? 'USD'
          : 0.0,
      value: QuantitativeValueObj,
    };
  }
  // jobBenefits
  if (
    Array.isArray(jobDetails?.jobBenefits) &&
    jobDetails?.jobBenefits.length > 0
  ) {
    jobPostingObj['jobBenefits'] = jobDetails?.jobBenefits;
  }
  jobPostingObj['directApply'] = true;
  return jobPostingObj;
};

// Main Component
export default function JobDetails(props: IProps) {
  const router = useRouter();
  const {
    categoryBasedSchema,
    jobDetails,
    isPremiumUser,
    isLoggedIn,
    themeType,
    isFreeJob,
    localization,
    deviceType,
    host,
    isSecondaryModalPopup,
    hiring_count,
    similarJobsItem,
    similarJobCategoryName,
    userInteractExpId,
    userAgent,
    isMatchFreeSchemaJob,
  } = props;
  // Meta title helpers
  const metaJobTitle = (
    title: string,
    companyName: string,
    location?: string,
    isMetaDesc: boolean = false
  ) => {
    if (!title) return '';
    const metaDesciption =
      isPremiumUser || jobDetails?.isFreeJob
        ? `Check out this ${title} job${
            companyName ? ' at ' + companyName : ''
          }${location ? ' in ' + location : ''}. Remote.co features ${
            jobDetails.remoteOptions[0]
          }.`
        : `Check out this ${title} job. Remote.co features ${jobDetails.remoteOptions[0]}.`;
    const titleContent =
      isPremiumUser || jobDetails?.isFreeJob
        ? `${title} job${companyName ? ' at ' + companyName : ''}${
            location ? ' in ' + location : ''
          }`
        : `${title} job`;
    return isMetaDesc ? metaDesciption : titleContent;
  };

  // Precompute meta tag values for DRYness and clarity
  const metaTitle = metaJobTitle(
    jobDetails?.title,
    jobDetails?.company?.name,
    jobDetails?.jobLocations
  );
  const metaDescription = metaJobTitle(
    jobDetails?.title,
    jobDetails?.company?.name,
    jobDetails?.jobLocations,
    true
  );
  const jobDetailItem = { getRmcCategories, ...jobDetails };
  const [showJobSearchSteps, setShowJobSearchSteps] = useState(false);
  const [showExperiment, setShowExperiment] = useState(userInteractExpId === 3);
  const [savedJobsArray, setSavedJobsArray] = useState<number[]>([]);
  const [shouldShowSearchStepDrawer, setShouldShowSearchStepDrawer] = useState(
    !isLoggedIn
  );
  const [hiringCount, setHiringCount] = useState<number | undefined>();
  const [guids, setGuid] = useState<any>(getOrUpdateGUID()); // for event tracking
  const isMobileView = deviceType === 'mobile';
  const isDesktopView = useDeviceType(props.deviceType) === 'desktop';

  useLandingPageTracking();

  useEffect(() => {
    if (isPremiumUser && isLoggedIn) {
      jobViewed(jobDetails.id);
      jobSearchResultTrackEvent(
        Action.Viewed,
        null,
        ViewType.Separate_Tab,
        null,
        jobDetails,
        null,
        null,
        null,
        null,
        null
      );
    }
  }, [jobDetails]);

  // Cleanup: Remove any legacy JobPosting schema script tags from the DOM if present (for current noindex pages)
  useEffect(() => {
    if (typeof window !== 'undefined' && jobDetails?.isFreeJob === false) {
      // Remove all JobPosting schema script tags
      const scripts = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((script) => {
        try {
          const json = JSON.parse(script.textContent || '');
          // Remove if it's a JobPosting schema or contains JobPosting in @graph
          if (
            json['@type'] === 'JobPosting' ||
            (Array.isArray(json['@graph']) &&
              json['@graph'].some((g) => g['@type'] === 'JobPosting'))
          ) {
            script.parentNode?.removeChild(script);
          }
        } catch {
          console.log(
            'Error parsing JSON from script tag, skipping removal for this tag.'
          );
        }
      });
    }
  }, [jobDetails?.isFreeJob]);

  useEffect(() => {
    const popCount = getDrawerPopCount();
    if (shouldShowSearchStepDrawer && popCount < 5) {
      getCompaniesHiringCount().then(setHiringCount);
      addDrawerListeners(setShowJobSearchSteps, undefined, true);
    }

    return () => {
      window.removeEventListener('scroll', () =>
        handleDrawerScroll(getScrollHeightDrawerExp(), setShowJobSearchSteps)
      );
    };
  }, []);

  //isExpSimilarJobsAllAllV2CVR
  const runSimilarJobsExperiment = useCallback(async () => {
    try {
      const visitorId = await ensureVisitorId(20, 1000);

      if (!visitorId) return;

      const expID = RMCPortalExperiments.rmcJobDetailsSimilarJobAllAllV2CVR.id;

      const expVariant = getExperimentVariant(expID);

      if (userInteractExpId === 3 || expVariant === 3) {
        setShowExperiment(true);
      } else if (
        (userInteractExpId === 0 || expVariant === 0) &&
        !isPremiumUser
      ) {
        // Only run experiment if no variant is set and user is not premium
        const visitorId = await ensureVisitorId(20, 1000);
        if (visitorId) {
          const exptData = await ConductExperimentV3(
            visitorId,
            RMCPortalExperiments.rmcJobDetailsSimilarJobAllAllV2CVR
          );

          if (exptData?.data?.variant === 3) {
            setShowExperiment(true);
          }
        }
      }
    } catch (error) {
      console.error('Visitor experiment conduction error:', error);
    }
  }, [isDesktopView, isPremiumUser]);

  useEffect(() => {
    runSimilarJobsExperiment();
  }, [runSimilarJobsExperiment]);

  const updateSaveJobState = (jobId: number, status: boolean) => {
    setSavedJobsArray((prevState) =>
      status ? [...prevState, jobId] : prevState.filter((id) => id !== jobId)
    );
  };

  const salaryData = parseSalaryRange(jobDetails?.salaryRange || '');
  let jobPostingObj = null;
  let paywall_schema = {};
  // Only include JobPosting schema if the page is indexable (not noindex)
  const isIndexable = jobDetails?.isFreeJob === true;
  if (isIndexable) {
    jobPostingObj = createJobPostingObject(
      { ...jobDetails, title: metaTitle },
      host,
      salaryData
    );
  }
  if (isFreeJob) {
    paywall_schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${metaTitle} - Remote.co`,
      url: `https://${host}/job-details/${jobDetails?.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Remote.co',
        url: `https://${host}`,
      },
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: 'False',
        cssSelector: '.job-details-paywall-wrapper',
      },
      isAccessibleForFree: 'False',
    };
  }

  if (!jobDetails) {
    return <ErrorPages statusCode={410} />;
  }

  const breadCrumbProps = {
    breadCrumbName: `${
      props.isPremiumUser ? 'public-jobs-page-premium' : 'public-jobs-page'
    }`,
    breadCrumbNameValue: [jobDetails?.title],
    breadCrumbsData: [
      {
        page: 'public-jobs-page',
        title: 'Public Jobs Page',
        children: [
          {
            name: 'Home',
            path: '/',
            static: false,
          },
          {
            name: 'Remote Jobs',
            path: '/remote-jobs',
            static: false,
          },
          {
            name: '{0}',
            path: '',
            static: true,
          },
        ],
      },
      {
        page: 'public-jobs-page-premium',
        title: 'Public Jobs Page',
        children: [
          {
            name: 'My Dashboard',
            path: '/dashboard',
            static: false,
          },
          {
            name: 'Remote Jobs',
            path: '/remote-jobs',
            static: false,
          },
          {
            name: '{0}',
            path: '',
            static: true,
          },
        ],
      },
    ],
  };
  const breadCrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://remote.co/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Remote Jobs',
        item: 'https://remote.co/remote-jobs',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: metaTitle + ' - Remote.co',
        item: 'https://remote.co/job-details/' + jobDetails?.slug,
      },
    ],
  };
  const categories = rmcCategoriesObj?.find(
    (item: any) => item?.category?.slug === props?.category
  );
  const { jobTypes, jobSchedules, careerLevel } = props?.jobDetails ?? {};
  const { category } = props;
  const matchCategory = (list: any) =>
    list?.find((item: any) => item.toLowerCase() === category);

  const getJobTypes = matchCategory(jobTypes);
  const getJobSchedules = matchCategory(jobSchedules);
  const getCareerLevel = matchCategory(careerLevel);

  const breadcrumbJobDetailsCat =
    category?.toLowerCase() === getJobTypes?.toLowerCase() ||
    category?.toLowerCase() === getJobSchedules?.toLowerCase() ||
    category?.toLowerCase() === getCareerLevel?.toLowerCase()
      ? getJobTypes || getJobSchedules || getCareerLevel
      : categories;

  const breadcrumbJobDetailsCatName =
    categories?.category?.name ?? breadcrumbJobDetailsCat;
  const breadCrumbNameWithCategories = {
    ...categories,
    ...breadcrumbJobDetailsCat,
    breadcrumbJobDetailsCatName: breadcrumbJobDetailsCatName,
  };

  const handleApplyJob = () => {
    addJobsCountTraits(USER_TRAITS_JOBS_COUNT.JOB_APPLICATIONS);
    applyJob(jobDetails.id, ApplyJobStatus.applied);
    trackJobSearchResult(Action.Applied, jobDetails.id);
  };
  const fetchJobData = async (jobID: string) => {
    if (!jobID) throw new Error('Job ID is required for tracking.');

    const jobDetailResp = await getJobDetailsV2(jobID);
    const jobDetails = jobDetailResp?.data;
    if (!jobDetails) throw new Error(`No job details found for ID: ${jobID}`);

    const guid = getGuidValue(setGuid);

    return { jobDetails, guid };
  };
  const determineAccessStatus = (jobDetails: any) => {
    if (isLoggedIn) {
      return isPremiumUser ? SearchType.Unlocked : SearchType.Locked;
    }
    return jobDetails?.isFreeJob ? SearchType.Unlocked : SearchType.Locked;
  };
  const trackJobSearchResult = async (
    action: string,
    jobID?: string,
    clickOption?: string
  ) => {
    const { jobDetails, guid } = await fetchJobData(jobID!);
    const accessStatus = determineAccessStatus(jobDetails);

    return jobSearchResultTrackEvent(
      action,
      getSearchType(),
      ViewType.Job_Post_Details,
      accessStatus,
      jobDetails,
      jobDetails,
      null,
      clickOption,
      null,
      guid
    );
  };

  // FAQ schema generation for free jobs.
  const shouldHideCompanyInFaq = jobDetails?.isFreeJob === false;
  const baseFaqItems = isMatchFreeSchemaJob
    ? generateFaqItems(jobDetails, {
        hideCompanyName: shouldHideCompanyInFaq,
      })
    : [];
  const faqSchema = isMatchFreeSchemaJob
    ? createFaqSchema(jobDetails, {
        hideCompanyName: shouldHideCompanyInFaq,
      })
    : null;

  // Add professional level question at second-to-last position (10th position)
  const faqItems =
    isMatchFreeSchemaJob &&
    jobDetails?.careerLevel &&
    jobDetails.careerLevel.length > 0
      ? [
          ...baseFaqItems.slice(0, -1), // All items except the last one
          {
            question: `Which professional level is required for this ${
              jobDetails?.title || 'this'
            } position${
              !shouldHideCompanyInFaq && jobDetails?.company?.name
                ? ' at ' + jobDetails.company.name
                : ''
            }?`,
            answer: Array.isArray(jobDetails.careerLevel)
              ? jobDetails.careerLevel.join(', ')
              : jobDetails.careerLevel,
          },
          baseFaqItems[baseFaqItems.length - 1], // Add the last item at the end
        ].filter(Boolean) // Remove any undefined items if baseFaqItems is empty
      : baseFaqItems;

  const jobSearchStepsProp = jobSearchStepsProps({
    isCloseAllowed: true,
    isLoggedIn: isLoggedIn,
    themeType: themeType,
    isCategoryPage: false,
    signupAndFind: localization.unlock_banner.signup_and_find,
    jobsData: jobDetailItem,
    categoryDetails: {},
    hiringCount: props.hiring_count,
    localization: localization,
    screenName: 'jobs by category',
    userInteractExpId: showExperiment,
  });

  const jobDetailsPageProp = jobDetailsPageProps({
    jobDetails: jobDetailItem,
    isPremiumUser: isPremiumUser,
    isLoggedIn: isLoggedIn,
    themeType: themeType,
    isFreeJob: isFreeJob,
    deviceType: deviceType,
    breadCrumbProps: breadCrumbProps,
    isJobDetailsPage: true,
    handleBackBtn: (e: React.SyntheticEvent) => {
      e.preventDefault();
      router.back();
    },
    localization: localization,
    saveJobState: { savedJobsArray, updateSaveJobState },
    isExperiment: deviceType === 'mobile' && !props.isPremiumUser,
    breadCrumbNameWithCategories: breadCrumbNameWithCategories,
    basePathRewrite: basePathRewrite,
    isSecondaryModalPopup: isSecondaryModalPopup,
    handleApplyJob: handleApplyJob,
    trackJobSearchResult: trackJobSearchResult,
    cetgoriesCount: cetgoriesCount,
    hiring_count: hiring_count,
    similarJobs: similarJobsItem,
    userInteractExpId: showExperiment,
    assetUrl: getAbsoluteURL,
    // unlockBannerExperiment: getunlockBannerExperiment(isUnlockBannerExpVariant),
    isJobDetailsFaqJEnabled: isMatchFreeSchemaJob,
    faqTitle: `FAQs About ${jobDetails?.title || 'this'} Jobs${
      !shouldHideCompanyInFaq && jobDetails?.company?.name
        ? ' at ' + jobDetails.company.name
        : ''
    }`,
    faqData: faqItems,
  });

  return (
    <>
      <Head>
        {jobDetails.company && (
          <meta property="og:image" content={jobDetails.company.logo} />
        )}
        <title>{`${metaTitle} - Remote.co`}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${metaTitle}.`} />
        <meta property="og:description" content={metaDescription} />
        {isProdEnv && <link rel="canonical" href={props.url} />}
        {/* <meta name="robots" content="max-image-preview:large, noodp" /> */}
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={props.url} />
        <meta property="og:site_name" content="Remote.co" />
        <meta
          property="article:publisher"
          content="https://www.facebook.com/remote.co"
        />
        <meta
          property="article:modified_time"
          content="2024-11-13T02:06:00+00:00"
        />
        <meta property="og:image:width" content="100" />
        <meta property="og:image:height" content="100" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@remote_co" />
        <meta name="twitter:label1" content="Est. reading time" />
        <meta name="twitter:data1" content="4 minutes" />
        <meta name="isFreeJob" content={String(isFreeJob || 'false')} />
        {isFreeJob ? (
          <meta
            name="robots"
            content="index, follow, max-image-preview:large"
          />
        ) : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {faqSchema && isMatchFreeSchemaJob && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchema, null, isProdEnv ? 0 : 2),
            }}
          />
        )}
        {/* Only inject JobPosting schema if indexable */}
        {isIndexable && jobPostingObj && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(
                {
                  '@context': 'https://schema.org',
                  '@graph': isMobileView
                    ? [jobPostingObj]
                    : [breadCrumbSchema, jobPostingObj],
                },
                null,
                isProdEnv ? 0 : 2
              ), // Pretty print JSON in development
            }}
          />
        )}
        {isFreeJob && paywall_schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(paywall_schema, null, isProdEnv ? 0 : 2),
            }}
          />
        )}
        <SiteNavigationSchemaScript />
      </Head>
      <Script
        src={`${
          getENVPath().NEXT_PUBLIC_ASSET_URL
        }/ui-experimentation/cdn/experiment-sync.min.js`}
        strategy="afterInteractive"
      />
      <Script id="experiment-script" strategy="afterInteractive">
        {`
          window.fireExperimentEvent = ${fireExperimentEvent.toString()};
        `}
      </Script>
      <JobSearchSteps jobSearchStepsProps={jobSearchStepsProp} />
      <JobDetailsPage {...jobDetailsPageProp} />
    </>
  );
}

// Server-Side Props
export const getServerSideProps: GetServerSideProps<any> = async ({
  query,
  req,
  res,
}: GetServerSidePropsContext) => {
  if (!query.companyslug) {
    return { notFound: true };
  }

  const commonSSRProps = await commonPropsForSSR(req, 'report_job_loc');
  const { cookieHeader, deviceType, isPremiumUser, cookies } = commonSSRProps;

  let enableFreeJobsView = !isPremiumUser
    ? getENVPath().NEXT_FEATURE_FLAG_FREE_JOBS_VIEW
    : false;

  let companySlug = query.companyslug as string;
  if (companySlug === 'undefined') {
    return { notFound: true };
  }

  let jobId = checkForGuid(companySlug) || '';
  if (!jobId) {
    const lastNum = companySlug.substring(companySlug.lastIndexOf('-') + 1);
    if (!isNaN(parseInt(lastNum))) {
      jobId = lastNum;
    }
  }

  const apiEndpoints = {
    job_details: getEJendPoints().job_details_v2,
    country_list: getEBendPoints().country_list,
    hiring_count: getEJendPoints().hiring_count,
  };
  if (apiEndpoints.job_details) {
    modifyJobDetails(apiEndpoints.job_details, jobId);
  }

  // let userInteractExpId = '';
  // if (deviceType === 'mobile') {
  //   userInteractExpId = RMCPortalExperiments.rmcUserInteractionMobile.id;
  // } else {
  //   userInteractExpId = RMCPortalExperiments.rmcUserInteractionDesktop.id;
  // }

  // For mobile devices, variant 3 is used as the baseline because it represents the control group for mobile paywall experiments.
  // For desktop devices, variant 0 is used as the baseline as it represents the control group for desktop paywall experiments.
  let expData = deviceType === 'mobile' ? 3 : 0;

  let similarJobs = [];
  let similarJobCategoryName = '';
  let selectedCategory = readCookie('selectedCategory', cookieHeader);
  if (expData && expData == 5 && selectedCategory) {
    let categorizedJobs = await getFreeJobsByCategory(
      { size: '8', cookieHeader, req },
      [selectedCategory]
    );

    if (
      categorizedJobs &&
      categorizedJobs[0] &&
      categorizedJobs[0].jobDetails
    ) {
      similarJobCategoryName = categorizedJobs[0].name;
      similarJobs = categorizedJobs[0].jobDetails.filter(
        (item: any) => item.id != jobId
      );
    }
  }

  const [functionProps, similarJobsItem] = await Promise.all([
    apiCallList({
      apiEndpoints,
      cookieHeader,
      req,
    }).then((data) => {
      const rawJobDetails = data.job_details;
      normalizeJobDetailsV2(rawJobDetails);
      const { isFreeJob = false, jobDetails = {} } =
        checkIsFreeJobAvailable(rawJobDetails);
      const hiring_count = data.hiring_count || {
        jobsCount: 0,
        companiesCount: 0,
      };
      return { isFreeJob, jobDetails, hiring_count };
    }),
    getSimilarJobs(jobId, 6, cookieHeader)
      .then((data) => {
        // Sanitize response to remove undefined values (Next.js cannot serialize undefined)
        return JSON.parse(JSON.stringify(data));
      })
      .catch(() => null),
  ]);

  /**
   *  Check The category id of the job.
   *  If the job is under category 1 or 4, then check the cookie for free jobs.
   *  Check allowed free jobs view limit is reached or not.
   *  If the limit is reached, then show locked job details page.
   *  If the limit is not reached, then show the job details page.
   *  And add this job under the category in the cookie.
   *  If the job is under free jobs category, then we will get the job details from the cookie.
   *  If the job is not under free jobs category, then we will get the job
   */

  let isLiesUnderFreeJobs = '';
  if (enableFreeJobsView) {
    const cookieVal =
      readFeatureCookieByKey('experimentBasedFreeRemoteJobs', cookieHeader) ||
      '';

    const cachedCategories = functionProps.jobDetails?.cachedInCategory;
    if (Array.isArray(cachedCategories)) {
      if (cachedCategories.includes('1')) {
        isLiesUnderFreeJobs = '1';
      } else if (cachedCategories.includes('4')) {
        isLiesUnderFreeJobs = '4';
      }
    }
    if (isLiesUnderFreeJobs) {
      let freeJobViewQuota;
      try {
        freeJobViewQuota = await getFreeJobViewQuota(
          String(isLiesUnderFreeJobs),
          commonSSRProps?.cookieHeader || req.headers.cookie
        );
      } catch (err) {
        // Could not get quota, treat as locked
        functionProps.jobDetails.isFreeJob = false;
        // functionProps.jobDetails.description = null;
      }

      const { allowViewFreeJob, viewedJobIds } = freeJobViewQuota || {};

      if (allowViewFreeJob) {
        let freeJobsObj: Record<string, string[]> = {};
        try {
          if (typeof cookieVal === 'string' && cookieVal !== '') {
            freeJobsObj = JSON.parse(cookieVal);
          } else if (typeof cookieVal === 'object' && cookieVal !== null) {
            freeJobsObj = cookieVal;
          }
        } catch {
          freeJobsObj = {};
        }
        const catId = String(isLiesUnderFreeJobs);
        freeJobsObj[catId] = freeJobsObj[catId] || [];
        if (!freeJobsObj[catId].includes(jobId)) {
          freeJobsObj[catId].push(jobId);
        }
        setFeatureCookieByKey({
          key: 'experimentBasedFreeRemoteJobs',
          value: JSON.stringify(freeJobsObj),
          cookieHeader: cookieHeader,
          res,
        });
      } else {
        let freeJobsByCategoryList = [];
        let foundValue: any = null;
        let foundKey: boolean = false;
        if (Array.isArray(viewedJobIds) && viewedJobIds.includes(jobId)) {
          foundKey = true;
        }
        if (foundKey) {
          try {
            freeJobsByCategoryList = await getFreeRemoteJobsByCategory(
              [isLiesUnderFreeJobs],
              cookieHeader
            );
            const categoryItem = freeJobsByCategoryList.find(
              (item: any) => item.key == isLiesUnderFreeJobs
            );
            if (categoryItem && Array.isArray(categoryItem.value)) {
              foundValue = categoryItem.value.find((i: any) => i.id == jobId);
            }
            if (foundValue) {
              functionProps.jobDetails = foundValue;
            } else {
              functionProps.jobDetails.isFreeJob = false;
              // functionProps.jobDetails.description = null;
            }
          } catch {
            functionProps.jobDetails.isFreeJob = false;
            // functionProps.jobDetails.description = null;
          }
        } else {
          functionProps.jobDetails.isFreeJob = false;
          // functionProps.jobDetails.description = null;
        }
      }
    }
  }

  if (!functionProps?.jobDetails?.title) {
    return response410(
      res,
      `Unable to get the title for job details, ${JSON.stringify(
        functionProps.jobDetails
      )}`
    );
  }
  const getCompanyData = (data: any, companySlug: string) => {
    return data.find((company?: any) => {
      return company.rmc === companySlug || company.fj === companySlug;
    });
  };
  if (functionProps?.jobDetails && functionProps?.jobDetails.company) {
    const companyMapping = getCompanyData(
      companiesMapping,
      functionProps?.jobDetails?.company?.slug
    );
    if (!companyMapping) {
      functionProps.jobDetails.isrmcwpJob = true;
    }
  }
  const getCompanyInformation = getCompanyData(
    companiesMapping,
    functionProps?.jobDetails?.company?.slug
  );

  functionProps.jobDetails = processJobDetailsForExperiment(
    functionProps.jobDetails,
    deviceType,
    cookieHeader,
    isPremiumUser,
    res
  );

  if (
    functionProps &&
    functionProps.jobDetails &&
    functionProps.jobDetails.company
  ) {
    functionProps.jobDetails.company = {
      ...functionProps.jobDetails.company,
      ...(getCompanyInformation || null),
    };
  }

  // Fetch similar jobs in SSR

  const { category } = query;

  const experimentSimilarJobs = () => {
    let expVarientData = 0;
    try {
      const expID = RMCPortalExperiments.rmcJobDetailsSimilarJobAllAllV2CVR.id;

      const expVariant = getExperimentVariant(expID, cookieHeader);
      if (expVariant === 3) {
        expVarientData = expVariant;
      }
    } catch (error) {
      console.error('Visitor experiment conduction error:', error);
    }
    return expVarientData;
  };
  let isMatchFreeSchemaJob = false;
  return {
    props: {
      ...functionProps,
      ...commonSSRProps,
      similarJobsItem: similarJobsItem,
      userInteractExpId: experimentSimilarJobs(),
      url: getfullUrl(req),
      host: getHostValue(req.headers.host || ''),
      userAgent: req.headers['user-agent'] || '',
      category: category || null,
      isSecondaryModalPopup: true,
      isMatchFreeSchemaJob: isMatchFreeSchemaJob,
      // unlockBannerExperiment: expPaywallData || 0,
    },
  };
};
